import { cleanInput } from '@exodus/basic-utils';
import type NumberUnit from '@exodus/currency';
import FancyAmount from '@passkeys/ui/fancy-amount';
import SegmentedControl from '@passkeys/ui/segmented-control';
import { useCallback, useEffect, useState } from 'react';

export const DEFAULT_FIAT_AMOUNTS = [30, 50, 100];

export interface HeroProps {
  amount: NumberUnit;
  onAmountChange: (amount: NumberUnit) => void;
}

function Hero({ amount, onAmountChange }: HeroProps) {
  const [activeIndex, setActiveIndex] = useState<number | undefined>();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [amountRaw, setAmountRaw] = useState(
    amount.toDefaultString({ unit: false }),
  );

  const updateActiveSegment = useCallback((value: string | undefined) => {
    if (!value) {
      return;
    }

    const index = DEFAULT_FIAT_AMOUNTS.indexOf(Number(value));
    setActiveIndex(index === -1 ? undefined : index);
  }, []);

  const handleAmountRawChange = (newAmountRaw) => {
    if (newAmountRaw && !/^\d*(\.)?(\d*)?$/.test(newAmountRaw)) {
      return;
    }

    setAmountRaw(newAmountRaw);
    onAmountChange(amount.defaultUnit(cleanInput(newAmountRaw)));
    updateActiveSegment(newAmountRaw);
  };

  useEffect(
    () => {
      updateActiveSegment(amount.toDefaultString({ unit: false }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateActiveSegment],
  );

  return (
    <div className="flex w-full flex-col items-center gap-y-6 pb-4 pt-6">
      <div className="h-[60px] max-w-full" data-testid="buy-form-hero-amount">
        {isFocused ? (
          <input
            autoFocus
            className="h-[52px] w-full bg-gray-100 bg-transparent text-center text-[52px] font-medium text-black outline-0 placeholder:text-gray-400 theme-dark:text-white theme-dark:placeholder:text-neutral-200"
            onBlur={() => setIsFocused(false)}
            onChange={(event) => handleAmountRawChange(event.target.value)}
            value={amountRaw}
            placeholder="0.00"
            data-testid="buy-form-amount-input"
          />
        ) : (
          <div onClick={() => setIsFocused(true)}>
            <FancyAmount value={amount.toDefaultNumber()} />
          </div>
        )}
      </div>

      <SegmentedControl
        activeIndex={activeIndex}
        className="w-full max-w-64"
        segments={DEFAULT_FIAT_AMOUNTS.map((defaultAmount) => ({
          label: `$${defaultAmount}`,
          onClick: () => handleAmountRawChange(defaultAmount.toString()),
        }))}
        monospace
      />
    </div>
  );
}

export default Hero;
